// ---------------------------------------------------------------------------
// compliance-hub.content.extra.ts — FR, DE, JA, ZH translations
// ---------------------------------------------------------------------------
import type { ComplianceHubContent } from './compliance-hub.content';

export const fr: ComplianceHubContent = {
  heroBadge: '6 Régions Couvertes',
  heroTitle: 'Conformité Régionale',
  heroSubtitle:
    'Calcul automatique des taxes, enregistrement des clients et protection des données dans 6 régions mondiales. Restez conforme sans effort grâce à des cadres réglementaires intégrés adaptés à chaque juridiction.',
  regions: {
    'eu-uk': {
      region: 'UE et Royaume-Uni',
      items: [
        'Protection des données GDPR et droit à l\'effacement',
        'Automatisation TVA multi-taux (0-27%)',
        'Conformité des paiements PSD2/SCA',
        'Gestion du consentement des cookies ePrivacy',
      ],
    },
    'united-states': {
      region: 'États-Unis',
      items: [
        'Calcul des taxes état par état',
        'Auto-évaluation PCI DSS',
        'Rapports de conformité ADA',
        'Automatisation de la taxe de séjour locale',
      ],
    },
    'uae-middle-east': {
      region: 'EAU et Moyen-Orient',
      items: [
        'Suivi des frais Tourism Dirham',
        'Facturation TVA 5% avec TRN',
        'Automatisation des frais municipaux',
        'Support multilingue arabe',
      ],
    },
    'thailand-sea': {
      region: 'Thaïlande et Asie du Sud-Est',
      items: [
        'Déclarations d\'immigration TM30',
        'Automatisation TVA 7%',
        'Conformité fiscale hôtelière thaïlandaise',
        'Enregistrement des clients étrangers',
      ],
    },
    australia: {
      region: 'Australie et Océanie',
      items: [
        'Déclarations GST 10% et BAS',
        'Taxes touristiques des États',
        'Conformité au Privacy Act',
        'Directives de tarification ACCC',
      ],
    },
    japan: {
      region: 'Japon',
      items: [
        'Taxe d\'hébergement par ville',
        'Support des licences Ryokan',
        'Registre des clients étrangers (\u6CD5\u5B9A\u5E33\u7C3F)',
        'Conformité des factures JCT',
      ],
    },
    india: {
      region: 'Inde',
      items: [
        'Automatisation des tranches GST 12%/18%',
        'Rapports C-Form et FRRO',
        'Conformité sécurité alimentaire FSSAI',
        'Protection des données DPDP',
      ],
    },
  },
  ctaTitle: 'Besoin de conformité pour une autre région ?',
  ctaSubtitle:
    'HotelStack prend en charge les cadres de conformité personnalisés. Contactez notre équipe pour discuter des exigences fiscales, juridiques et de protection des données spécifiques à votre région.',
};

export const de: ComplianceHubContent = {
  heroBadge: '6 Regionen Abgedeckt',
  heroTitle: 'Regionale Compliance',
  heroSubtitle:
    'Automatische Steuerberechnung, Gästeregistrierung und Datenschutz in 6 globalen Regionen. Bleiben Sie mühelos konform mit integrierten regulatorischen Rahmenwerken, die auf jede Gerichtsbarkeit zugeschnitten sind.',
  regions: {
    'eu-uk': {
      region: 'EU und Vereinigtes Königreich',
      items: [
        'GDPR-Datenschutz und Recht auf Löschung',
        'Mehrstufige MwSt.-Automatisierung (0-27%)',
        'PSD2/SCA-Zahlungskonformität',
        'ePrivacy-Cookie-Einwilligungsmanagement',
      ],
    },
    'united-states': {
      region: 'Vereinigte Staaten',
      items: [
        'Steuerberechnung nach Bundesstaat',
        'PCI DSS-Selbstbewertung',
        'ADA-Konformitätsberichte',
        'Automatisierung der lokalen Belegungssteuer',
      ],
    },
    'uae-middle-east': {
      region: 'VAE und Naher Osten',
      items: [
        'Tourism Dirham-Gebührenerfassung',
        '5% MwSt.-Rechnungsstellung mit TRN',
        'Automatisierung der Gemeindegebühren',
        'Mehrsprachige Arabisch-Unterstützung',
      ],
    },
    'thailand-sea': {
      region: 'Thailand und Südostasien',
      items: [
        'TM30-Einwanderungsmeldung',
        '7% MwSt.-Automatisierung',
        'Thailändische Hotelsteuerkonformität',
        'Registrierung ausländischer Gäste',
      ],
    },
    australia: {
      region: 'Australien und Ozeanien',
      items: [
        '10% GST-Berichterstattung und BAS',
        'Staatliche Tourismusabgaben',
        'Privacy Act-Konformität',
        'ACCC-Preisrichtlinien',
      ],
    },
    japan: {
      region: 'Japan',
      items: [
        'Stadtspezifische Beherbergungssteuer',
        'Ryokan-Lizenzunterstützung',
        'Ausländisches Gästeregister (\u6CD5\u5B9A\u5E33\u7C3F)',
        'JCT-Rechnungskonformität',
      ],
    },
    india: {
      region: 'Indien',
      items: [
        'GST 12%/18%-Stufenautomatisierung',
        'C-Form- und FRRO-Berichte',
        'FSSAI-Lebensmittelsicherheitskonformität',
        'DPDP-Datenschutz',
      ],
    },
  },
  ctaTitle: 'Compliance für eine andere Region benötigt?',
  ctaSubtitle:
    'HotelStack unterstützt individuelle Compliance-Rahmenwerke. Kontaktieren Sie unser Team, um regionale Steuer-, Rechts- und Datenschutzanforderungen zu besprechen.',
};

export const ja: ComplianceHubContent = {
  heroBadge: '6地域対応',
  heroTitle: '地域別コンプライアンス',
  heroSubtitle:
    '6つのグローバル地域における自動税計算、宿泊客登録、データ保護。各管轄区域に合わせた規制フレームワークにより、簡単にコンプライアンスを維持できます。',
  regions: {
    'eu-uk': {
      region: 'EU・英国',
      items: [
        'GDPRデータ保護と消去権',
        '複数税率VAT自動化 (0-27%)',
        'PSD2/SCA決済コンプライアンス',
        'ePrivacy Cookie同意管理',
      ],
    },
    'united-states': {
      region: 'アメリカ合衆国',
      items: [
        '州別税計算',
        'PCI DSS自己評価',
        'ADAコンプライアンス報告',
        '地方宿泊税自動化',
      ],
    },
    'uae-middle-east': {
      region: 'UAE・中東',
      items: [
        'Tourism Dirham手数料追跡',
        '5% VAT請求書発行（TRN付き）',
        '自治体手数料自動化',
        '多言語アラビア語対応',
      ],
    },
    'thailand-sea': {
      region: 'タイ・東南アジア',
      items: [
        'TM30入国管理報告',
        '7% VAT自動化',
        'タイのホテル税コンプライアンス',
        '外国人宿泊客登録',
      ],
    },
    australia: {
      region: 'オーストラリア・オセアニア',
      items: [
        '10% GST報告とBAS',
        '州観光税',
        'Privacy Actコンプライアンス',
        'ACCC価格ガイドライン',
      ],
    },
    japan: {
      region: '日本',
      items: [
        '都市別宿泊税',
        '旅館営業許可支援',
        '外国人宿泊者名簿（法定帳簿）',
        'JCTインボイスコンプライアンス',
      ],
    },
    india: {
      region: 'インド',
      items: [
        'GST 12%/18%スラブ自動化',
        'C-Form・FRRO報告',
        'FSSAI食品安全コンプライアンス',
        'DPDPデータ保護',
      ],
    },
  },
  ctaTitle: '他の地域のコンプライアンスが必要ですか？',
  ctaSubtitle:
    'HotelStackはカスタムコンプライアンスフレームワークに対応しています。地域固有の税務、法務、データ保護要件についてチームにお問い合わせください。',
};

export const zh: ComplianceHubContent = {
  heroBadge: '覆盖6个地区',
  heroTitle: '区域合规',
  heroSubtitle:
    '在6个全球地区实现自动税务计算、住客登记和数据保护。通过针对各辖区定制的内置监管框架，轻松保持合规。',
  regions: {
    'eu-uk': {
      region: '欧盟与英国',
      items: [
        'GDPR数据保护与删除权',
        '多税率增值税自动化 (0-27%)',
        'PSD2/SCA支付合规',
        'ePrivacy Cookie同意管理',
      ],
    },
    'united-states': {
      region: '美国',
      items: [
        '逐州税务计算',
        'PCI DSS自我评估',
        'ADA合规报告',
        '地方住宿税自动化',
      ],
    },
    'uae-middle-east': {
      region: '阿联酋与中东',
      items: [
        'Tourism Dirham费用追踪',
        '5%增值税开票（含TRN）',
        '市政费用自动化',
        '多语言阿拉伯语支持',
      ],
    },
    'thailand-sea': {
      region: '泰国与东南亚',
      items: [
        'TM30移民申报',
        '7%增值税自动化',
        '泰国酒店税合规',
        '外国住客登记',
      ],
    },
    australia: {
      region: '澳大利亚与大洋洲',
      items: [
        '10% GST申报与BAS',
        '州旅游税',
        'Privacy Act合规',
        'ACCC定价指南',
      ],
    },
    japan: {
      region: '日本',
      items: [
        '城市特定住宿税',
        '旅馆许可证支持',
        '外国住客登记簿（法定帳簿）',
        'JCT发票合规',
      ],
    },
    india: {
      region: '印度',
      items: [
        'GST 12%/18%税率自动化',
        'C-Form与FRRO申报',
        'FSSAI食品安全合规',
        'DPDP数据保护',
      ],
    },
  },
  ctaTitle: '需要其他地区的合规支持？',
  ctaSubtitle:
    'HotelStack支持自定义合规框架。联系我们的团队，讨论特定地区的税务、法律和数据保护要求。',
};
